import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_URL } from "../config/constants";
import "../assets/css/Inventory.css";

const Inventory = () => {
  const { t, i18n } = useTranslation();
  const fallbackImage =
    "https://placehold.co/800x500/e5e7eb/111827?text=Car+Image";

  const [cars, setCars] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("UAE");
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [sliderIndexByCar, setSliderIndexByCar] = useState({});
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [lightboxTouchStartX, setLightboxTouchStartX] = useState(null);
  const [lightboxTouchStartY, setLightboxTouchStartY] = useState(null);
  const [lightbox, setLightbox] = useState({
    isOpen: false,
    carKey: "",
    images: [],
    index: 0
  });

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const response = await fetch(`${API_URL}/inventory`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || "Failed to fetch inventory");
        }

        if (!Array.isArray(data.items)) {
          throw new Error("Invalid inventory response");
        }

        setCars(normalizeCars(data.items));
        setApiError(false);
      } catch (error) {
        const fallbackCars = t("inventory.cars", { returnObjects: true });
        setCars(normalizeCars(fallbackCars));
        setApiError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, [i18n.language, t]);

  const goToNextImage = (carKey, total) => {
    if (total <= 1) return;
    setSliderIndexByCar((prev) => ({
      ...prev,
      [carKey]: ((prev[carKey] || 0) + 1) % total
    }));
  };

  const goToPrevImage = (carKey, total) => {
    if (total <= 1) return;
    setSliderIndexByCar((prev) => ({
      ...prev,
      [carKey]: ((prev[carKey] || 0) - 1 + total) % total
    }));
  };

  const openLightbox = (carKey, images, index) => {
    setLightbox({
      isOpen: true,
      carKey,
      images,
      index
    });
  };

  const closeLightbox = () => {
    setLightbox({
      isOpen: false,
      carKey: "",
      images: [],
      index: 0
    });
  };

  const goToLightboxNext = () => {
    if (lightbox.images.length <= 1) return;
    setLightbox((prev) => ({
      ...prev,
      index: (prev.index + 1) % prev.images.length
    }));
  };

  const goToLightboxPrev = () => {
    if (lightbox.images.length <= 1) return;
    setLightbox((prev) => ({
      ...prev,
      index: (prev.index - 1 + prev.images.length) % prev.images.length
    }));
  };

  const handleCardTouchStart = (e) => {
    const touch = e.touches?.[0];
    if (!touch) return;
    setTouchStartX(touch.clientX);
    setTouchStartY(touch.clientY);
  };

  const handleCardTouchEnd = (e, carKey, total) => {
    const touch = e.changedTouches?.[0];
    if (!touch || touchStartX === null || touchStartY === null) return;

    const diffX = touchStartX - touch.clientX;
    const diffY = touchStartY - touch.clientY;

    setTouchStartX(null);
    setTouchStartY(null);

    // Only react to clear horizontal gestures.
    if (Math.abs(diffX) < 40 || Math.abs(diffX) < Math.abs(diffY)) return;

    if (diffX > 0) {
      goToNextImage(carKey, total);
    } else {
      goToPrevImage(carKey, total);
    }
  };

  const handleLightboxTouchStart = (e) => {
    const touch = e.touches?.[0];
    if (!touch) return;
    setLightboxTouchStartX(touch.clientX);
    setLightboxTouchStartY(touch.clientY);
  };

  const handleLightboxTouchEnd = (e) => {
    const touch = e.changedTouches?.[0];
    if (!touch || lightboxTouchStartX === null || lightboxTouchStartY === null) return;

    const diffX = lightboxTouchStartX - touch.clientX;
    const diffY = lightboxTouchStartY - touch.clientY;

    setLightboxTouchStartX(null);
    setLightboxTouchStartY(null);

    if (Math.abs(diffX) < 40 || Math.abs(diffX) < Math.abs(diffY)) return;

    if (diffX > 0) {
      goToLightboxNext();
    } else {
      goToLightboxPrev();
    }
  };

  const filteredCars = cars.filter(
    (car) => String(car.country || "UAE").toLowerCase() === selectedCountry.toLowerCase()
  );

  return (
    <section className="inventory-page pb-5">
      <div className="container new-container">
        <div className="container-in-pd">
          <div className="inventory-header text-center mb-5">
            <h1>{t("inventory.title")}</h1>
            <p>{t("inventory.subtitle")}</p>
          </div>

          <div className="inventory-toggle-wrap">
            <div className="inventory-toggle">
              <div
                className="inventory-toggle-slider"
                style={{ transform: selectedCountry === "UAE" ? "translateX(0)" : "translateX(100%)" }}
              />
              <button
                type="button"
                id="inventory-toggle-uae"
                className={`inventory-toggle-btn${selectedCountry === "UAE" ? " active" : ""}`}
                onClick={() => setSelectedCountry("UAE")}
              >
                <span className="fi fi-ae inventory-toggle-flag" />
                UAE
              </button>
              <button
                type="button"
                id="inventory-toggle-india"
                className={`inventory-toggle-btn${selectedCountry === "India" ? " active" : ""}`}
                onClick={() => setSelectedCountry("India")}
              >
                <span className="fi fi-in inventory-toggle-flag" />
                India
              </button>
            </div>
          </div>

          {loading && <p className="inventory-note">{t("inventory.loading")}</p>}

          {!loading && apiError && (
            <p className="inventory-note">{t("inventory.fallbackNote")}</p>
          )}

          {!loading && filteredCars.length === 0 && (
            <p className="inventory-note">{t("inventory.empty")}</p>
          )}

          <div className="row g-4">
            {filteredCars.map((car, index) => (
              <div
                className="col-md-6 col-lg-4"
                key={car.id || `${car.name}-${index}`}
              >
                <article className="inventory-card h-100">
                  <div className="inventory-slider">
                    <img
                      src={
                        car.images[sliderIndexByCar[car.id || car.name] || 0] ||
                        fallbackImage
                      }
                      alt={car.name || "Car"}
                      className="inventory-card-image"
                      onTouchStart={handleCardTouchStart}
                      onTouchEnd={(e) =>
                        handleCardTouchEnd(e, car.id || car.name, car.images.length)
                      }
                      onClick={() =>
                        openLightbox(
                          car.id || car.name,
                          car.images,
                          sliderIndexByCar[car.id || car.name] || 0
                        )
                      }
                      onError={(e) => {
                        e.currentTarget.src = fallbackImage;
                      }}
                    />
                    {car.images.length > 1 && (
                      <>
                        <button
                          type="button"
                          className="inventory-slider-btn prev"
                          onClick={(e) => {
                            e.stopPropagation();
                            goToPrevImage(car.id || car.name, car.images.length);
                          }}
                        >
                          {"<"}
                        </button>
                        <button
                          type="button"
                          className="inventory-slider-btn next"
                          onClick={(e) => {
                            e.stopPropagation();
                            goToNextImage(car.id || car.name, car.images.length);
                          }}
                        >
                          {">"}
                        </button>
                        <div className="inventory-slider-dots">
                          {car.images.map((_, imgIndex) => (
                            <span
                              key={`${car.id || car.name}-dot-${imgIndex}`}
                              className={`inventory-dot ${
                                (sliderIndexByCar[car.id || car.name] || 0) ===
                                imgIndex
                                  ? "active"
                                  : ""
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="inventory-badge-row">
                    <div
                      className={`inventory-card-badge ${
                        String(car.status || "").toLowerCase() === "sold"
                          ? "inventory-card-badge-sold"
                          : String(car.status || "").toLowerCase() === "reserved"
                          ? "inventory-card-badge-reserved"
                          : ""
                      }`}
                    >
                      {car.status}
                    </div>
                    {car.regionalSpec && (
                      <div className="inventory-card-badge inventory-card-badge-secondary">
                        {car.regionalSpec}
                      </div>
                    )}
                  </div>
                  <h3>{car.name}</h3>
                  <p className="inventory-card-price">{car.price}</p>
                  <ul className="inventory-specs">
                    {(car.specs || []).map((spec, specIndex) => (
                      <li key={`${car.name}-spec-${specIndex}`}>{spec}</li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    state={{ carName: car.name, prefillBuying: true }}
                    className="btn c-btn w-100 inventory-enquire-btn"
                  >
                    {t("inventory.cta")}
                  </Link>
                </article>
              </div>
            ))}
          </div>
        </div>
      </div>

      {lightbox.isOpen && (
        <div className="inventory-lightbox" onClick={closeLightbox}>
          <button
            type="button"
            className="inventory-lightbox-close"
            onClick={closeLightbox}
          >
            x
          </button>

          <div
            className="inventory-lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.images[lightbox.index] || fallbackImage}
              alt="Car preview"
              className="inventory-lightbox-image"
              onTouchStart={handleLightboxTouchStart}
              onTouchEnd={handleLightboxTouchEnd}
              onError={(e) => {
                e.currentTarget.src = fallbackImage;
              }}
            />

            {lightbox.images.length > 1 && (
              <>
                <button
                  type="button"
                  className="inventory-lightbox-nav prev"
                  onClick={goToLightboxPrev}
                >
                  {"<"}
                </button>
                <button
                  type="button"
                  className="inventory-lightbox-nav next"
                  onClick={goToLightboxNext}
                >
                  {">"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

const normalizeCars = (cars = []) =>
  (cars || [])
    .map((car, index) => ({
      ...car,
      id: car.id || `fallback-${index}`,
      images:
        Array.isArray(car.images) && car.images.length > 0
          ? car.images
          : car.imageUrl
          ? [car.imageUrl]
          : [],
      _originalIndex: index
    }))
    .sort((a, b) => {
      const priority = {
        available: 1,
        reserved: 2,
        sold: 3
      };

      const aStatus = String(a.status || "").toLowerCase();
      const bStatus = String(b.status || "").toLowerCase();

      const aPriority = priority[aStatus] || 99;
      const bPriority = priority[bStatus] || 99;

      if (aPriority !== bPriority) return aPriority - bPriority;
      return a._originalIndex - b._originalIndex;
    })
    .map(({ _originalIndex, ...car }) => ({
      ...car,
      country: normalizeCountry(car.country)
    }));

const normalizeCountry = (country) => {
  const value = String(country || "").trim().toLowerCase();
  if (value === "india") return "India";
  return "UAE";
};

export default Inventory;
